import React, {useContext, useEffect, useState} from 'react';
import {useKeycloak} from '@react-keycloak/web';
import {useIntl} from 'react-intl';
import {useDropzone} from 'react-dropzone';
import moment from 'moment';

import {
  Avatar,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Drawer,
  Form,
  Input,
  message,
  Row,
  Select,
  Spin,
} from 'antd';
import Address from 'pages/components/Address';
import CountrySelect from 'pages/components/CountrySelect';
import {PatientContext} from './PatientContext';

import {
  createPatient,
  updatePatient,
  uploadPhoto,
} from 'services/PatientService';
import {handleError} from 'utils/MessageUtils';
import {isEmptyObject} from 'utils/Utils';
import {DEFAULT_AVATAR_URL} from 'shared/constants/AppConst';

const PatientEditor = () => {
  const {keycloak} = useKeycloak();
  const intl = useIntl();
  const [form] = Form.useForm();

  const headers = {
    Authorization: `Bearer ${keycloak?.token}`,
    'Accept-Language': intl.locale,
  };

  const patientContext = useContext(PatientContext);
  const {selectedPatient, setSelectedPatient, editing, setEditing} =
    patientContext;
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editing) {
      form.resetFields();
    }
  }, [selectedPatient]);

  const onClose = () => {
    setEditing(false);
  };

  const onReset = () => {
    form.resetFields();
  };

  const onSave = (values) => {
    const data = {
      ...values,
      birthDate: values.birthDate?.format('DD/MM/YYYY'),
      address: isEmptyObject(values.address)
        ? null
        : {
            ...values.address,
            country: isEmptyObject(values.address?.country)
              ? null
              : {id: values.address.country.id},
            province: isEmptyObject(values.address?.province)
              ? null
              : {id: values.address.province.id},
            district: isEmptyObject(values.address?.district)
              ? null
              : {id: values.address.district.id},
            ward: isEmptyObject(values.address?.ward)
              ? null
              : {id: values.address.ward.id},
          },
      contact: isEmptyObject(values.contact)
        ? null
        : {
            ...values.contact,
            address: isEmptyObject(values.contact?.address)
              ? null
              : {
                  ...values.address,
                  country: isEmptyObject(values.contact?.address?.country)
                    ? null
                    : {id: values.contact.address.country.id},
                  province: isEmptyObject(values.contact?.address?.province)
                    ? null
                    : {id: values.contact.address.province.id},
                  district: isEmptyObject(values.contact?.address?.district)
                    ? null
                    : {id: values.contact.address.district.id},
                  ward: isEmptyObject(values.contact?.address?.ward)
                    ? null
                    : {id: values.contact.address.ward.id},
                },
          },
      nationality: isEmptyObject(values.nationality)
        ? null
        : {id: values.nationality.id},
    };

    if (selectedPatient?.id) {
      setLoading(true);
      updatePatient(headers, selectedPatient.id, data)
        .then((res) => {
          setSelectedPatient(res.data);

          setEditing(false);
          message.success(intl.messages['common.updated']);
        })
        .catch((error) => handleError(message, error))
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      createPatient(headers, data)
        .then((res) => {
          setSelectedPatient(res.data);

          setEditing(false);
          message.success(intl.messages['common.created']);
        })
        .catch((error) => handleError(message, error))
        .finally(() => setLoading(false));
    }
  };

  const {getRootProps, getInputProps} = useDropzone({
    accept: 'image/*',
    maxFiles: 1,
    maxSize: 1000000,
    onDrop: (acceptedFiles) => {
      const data = new FormData();
      data.append('photo', acceptedFiles[0]);
      setLoading(true);
      uploadPhoto(headers, selectedPatient.id, data)
        .then((res) => {
          setSelectedPatient(res.data);
        })
        .catch((error) => handleError(message, error))
        .finally(() => setLoading(false));
    },
  });

  return (
    <Drawer
      title={intl.messages['common.detail']}
      width={800}
      onClose={onClose}
      visible={editing}
      destroyOnClose={true}
      bodyStyle={{paddingBottom: 80}}>
      <Spin spinning={loading}>
        <Row>
          <Col span={10} />
          <Col span={4}>
            <Avatar
              style={{width: 100, height: 100, marginBottom: 10}}
              src={selectedPatient?.photoUrl || DEFAULT_AVATAR_URL}
            />
            {selectedPatient?.id && (
              <div {...getRootProps({className: 'dropzone'})}>
                <input {...getInputProps()} />
                <label htmlFor='icon-button-file'>
                  <Button type='primary' style={{width: 100}}>
                    {intl.messages['common.upload']}
                  </Button>
                </label>
              </div>
            )}
          </Col>
          <Col span={10} />
        </Row>
        {selectedPatient?.id && (
          <Row>
            <Col span={5} />
            <Col span={14}>
              <p>{intl.messages['userProfile.uploadPhoto']}</p>
            </Col>
            <Col span={5} />
          </Row>
        )}
        <Divider />
        <Form
          layout='vertical'
          form={form}
          onFinish={onSave}
          initialValues={{
            ...selectedPatient,
            birthDate: selectedPatient?.birthDate
              ? moment(selectedPatient.birthDate, 'DD/MM/YYYY')
              : null,
          }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name='name'
                label={intl.messages['patient.name']}
                rules={[
                  {
                    required: true,
                    message: `${intl.messages['patient.name']} ${intl.messages['validation.required']}`,
                  },
                ]}>
                <Input maxLength={100} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='active'
                label={intl.messages['common.enabled']}
                valuePropName='checked'>
                <Checkbox />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name='idn' label={intl.messages['patient.idn']}>
                <Input maxLength={20} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='ppn' label={intl.messages['patient.ppn']}>
                <Input maxLength={20} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name='hin' label={intl.messages['patient.hin']}>
                <Input maxLength={20} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={['nationality', 'id']}
                label={intl.messages['patient.nationality']}>
                <CountrySelect
                  onChange={(value) => {
                    form.setFieldsValue({nationality: {id: value}});
                  }}
                  onClear={() => {
                    form.setFieldsValue({nationality: null});
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name='email'
                label={intl.messages['common.email']}
                rules={[
                  {
                    type: 'email',
                    message: intl.messages['validation.email'],
                  },
                ]}>
                <Input maxLength={100} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='phone'
                label={intl.messages['common.phoneNumber']}>
                <Input maxLength={10} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name='birthDate'
                label={intl.messages['common.birthDate']}>
                <DatePicker format='DD/MM/YYYY' style={{width: '100%'}} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='gender' label={intl.messages['common.gender']}>
                <Select>
                  <Select.Option value='MALE'>
                    {intl.messages['common.gender.male']}
                  </Select.Option>
                  <Select.Option value='FEMALE'>
                    {intl.messages['common.gender.female']}
                  </Select.Option>
                  <Select.Option value='OTHER'>
                    {intl.messages['common.gender.other']}
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Address address={selectedPatient?.address} path={['address']} />

          <Row>
            <Col span={24}>
              <Form.Item
                name={['habit']}
                label={intl.messages['patient.habit']}>
                <Input.TextArea row={5} maxLength={500} showCount />
              </Form.Item>
            </Col>
          </Row>

          <Divider>{intl.messages['contact']}</Divider>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name={['contact', 'name']}
                label={intl.messages['contact.name']}>
                <Input maxLength={100} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name={['contact', 'email']}
                label={intl.messages['common.email']}
                rules={[
                  {
                    type: 'email',
                    message: intl.messages['validation.email'],
                  },
                ]}>
                <Input maxLength={100} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={['contact', 'phone']}
                label={intl.messages['common.phoneNumber']}>
                <Input maxLength={10} />
              </Form.Item>
            </Col>
          </Row>
          <Address
            address={selectedPatient?.contact?.address}
            path={['contact', 'address']}
          />

          <Divider />

          <Row>
            <Col span={9} />
            <Form.Item shouldUpdate noStyle>
              <Col span={3}>
                <Button onClick={onReset} style={{width: '90px'}}>
                  {intl.messages['common.reset']}
                </Button>
              </Col>
              <Col span={3}>
                <Button
                  type='primary'
                  htmlType='submit'
                  style={{width: '90px'}}>
                  {intl.messages['common.save']}
                </Button>
              </Col>
            </Form.Item>
            <Col span={9} />
          </Row>
        </Form>
      </Spin>
    </Drawer>
  );
};

export default PatientEditor;
